import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { normalizeUserData, getUserFields, prepareForUpdate } from '../services/userService';
import UserInfoFields from '../components/forms/UserInfoFields';
import DonationPreferencesFields from '../components/forms/DonationPreferencesFields';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const EditProfile = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOrgans, setSelectedOrgans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);

  // Form state for controlled components - pre-populate ALL fields from database
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    address: '',
    bloodType: '',
    gender: '',
    birthYear: null,
    isBloodDonor: false,
    isOrganDonor: false,
    bodyDonation: false,
    donationType: null,
    emergencyContact: {
      name: '',
      phone: '',
      relationship: 'Emergency Contact'
    }
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!authUser) {
      navigate('/signin');
      return;
    }

    // Normalize and set user from auth context
    const normalizedUser = normalizeUserData(authUser);
    setUser(normalizedUser);
    
    // Initialize form data with ALL fields from database
    const userFields = getUserFields(normalizedUser);
    setFormData({
      name: userFields.name || '',
      phone: userFields.phone || '',
      city: userFields.city || '',
      state: userFields.state || '',
      zipCode: userFields.zipCode || '',
      country: userFields.country || '',
      address: userFields.address || '',
      bloodType: userFields.bloodType || '',
      gender: userFields.gender || '',
      birthYear: userFields.birthYear || null,
      isBloodDonor: userFields.isBloodDonor || false,
      isOrganDonor: userFields.isOrganDonor || false,
      bodyDonation: userFields.bodyDonation || false,
      donationType: userFields.donationType || null,
      emergencyContact: userFields.emergencyContact || {
        name: '',
        phone: '',
        relationship: 'Emergency Contact'
      }
    });

    // Initialize selectedOrgans from user data
    const organs = userFields.organsPledged || [];
    setSelectedOrgans(organs);

    // Set country for state loading
    if (userFields.country) {
      setSelectedCountry(userFields.country);
    }

    fetchCountries();
    setLoading(false);
  }, [authUser, navigate, authLoading]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/regulations`);
      const sortedCountries = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sortedCountries);
      setLoadingCountries(false);
    } catch (error) {
      setLoadingCountries(false);
    }
  };

  useEffect(() => {
    if (selectedCountry && countries.length > 0) {
      fetchStates(selectedCountry);
    } else {
      setStates([]);
    }
  }, [selectedCountry, countries]);

  const fetchStates = async (countryName) => {
    setLoadingStates(true);
    try {
      const country = countries.find(c => c.name === countryName);
      if (country && country.code) {
        const response = await axios.get(`${API}/states/${country.code}`);
        setStates(response.data || []);
      } else {
        setStates([]);
      }
    } catch (error) {
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const handleOrganToggle = (organId) => {
    setSelectedOrgans(prev =>
      prev.includes(organId)
        ? prev.filter(id => id !== organId)
        : [...prev, organId]
    );
  };

  const handleFormChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Prepare update data with formData and selectedOrgans
      const updateData = {
        ...formData,
        organsPledged: selectedOrgans,
        birthYear: formData.birthYear ? parseInt(formData.birthYear) : null
      };

      // Transform to backend format (snake_case)
      const backendData = prepareForUpdate(updateData);

      // Call API to update user
      const response = await axios.put(`${API}/users/${user.id}`, backendData);
      
      // Normalize and update local state with response
      const updatedUser = normalizeUserData(response.data);
      
      // Update auth context and localStorage
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      
      toast({ 
        title: 'Profile Updated', 
        description: 'Your profile has been updated successfully.' 
      });
      
      // Navigate back to profile
      navigate('/profile');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to update profile. Please try again.';
      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/profile')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Profile
        </Button>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-gray-900 text-2xl">Edit Profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <UserInfoFields
                formData={formData}
                onChange={handleFormChange}
                errors={{}}
                countries={countries}
                states={states}
                selectedCountry={selectedCountry}
                loadingCountries={loadingCountries}
                loadingStates={loadingStates}
                onCountryChange={(value) => {
                  setSelectedCountry(value);
                  handleFormChange('country', value);
                }}
                onStateChange={(value) => {
                  handleFormChange('state', value);
                }}
                showGender={true}
                showBirthYear={true}
                showAddress={true}
                showCountry={true}
                showCity={true}
                showState={true}
                showZipCode={true}
                requiredFields={['name', 'phone', 'city', 'zipCode', 'country']}
              />

              <DonationPreferencesFields
                formData={formData}
                onChange={(field, value) => {
                  if (field === 'emergencyContact') {
                    setFormData(prev => ({
                      ...prev,
                      emergencyContact: value
                    }));
                  } else {
                    handleFormChange(field, value);
                  }
                }}
                selectedOrgans={selectedOrgans}
                onOrganToggle={handleOrganToggle}
                errors={{}}
                showBloodType={true}
                showDonationType={true}
                showOrgansSelection={true}
                showEmergencyContact={true}
              />
              
              <div className="flex gap-4 pt-4">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => navigate('/profile')}
                  className="flex-1 text-gray-900 border-gray-900 hover:bg-gray-100"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-red-700 hover:bg-red-900 text-white"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save All Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditProfile;

